import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import to from 'await-to-js';
import { toast } from 'react-toastify';
import { RootState } from './store';
import APIService from '../Utils/APIService';
import OperationData from '../Types/OperationData';
import { getUserType } from './auth.store';
import { showRouteDetailsModal } from './app-ui.store';
import { URLConfig } from '../Utils/features';

// ← Types
export interface TripsState {
  loading: boolean;
  routes: OperationData[];
  selectedRoute: any;
  selectedRouteLoading: boolean;
  confirmationDetailsData: any[];
}

const initialState: TripsState = {
  loading: false,
  routes: [],
  selectedRoute: {},
  selectedRouteLoading: false,
  confirmationDetailsData: [],
};

export const fetchRoutes = createAsyncThunk<
  any,
  { start: number; limit: number; licenseNo: string; startDate: string; endDate: string; tripName: string }
>('routes/get', async ({ start, limit, licenseNo, startDate, endDate, tripName }, { getState, rejectWithValue }) => {
  const userType = getUserType(getState() as RootState);
  const url = URLConfig.get_recent_routes[userType];
  if (!url) return rejectWithValue('Not available for this user type');
  const page = Math.floor(start / limit) + 1;
  const [err, data] = await to(
    APIService.get(url, {
      params: { page, page_size: limit, startDate, endDate, tripName, licenseNo },
    }),
  );
  if (err) {
    toast.error('Routes not loaded. Please try again!');
    rejectWithValue(err);
  }
  const body = data?.data;
  return Array.isArray(body) ? body : body?.data ?? [];
});

export const fetchRouteDetails = createAsyncThunk<any, { tripID: number; licenseNo: string }>(
  'routes/get-details',
  async ({ tripID }, { rejectWithValue, dispatch, getState }) => {
    const userType = getUserType(getState() as RootState);
    if (userType === 'admin') return rejectWithValue('Not available for this user type');
    const [err, data] = await to(APIService.get(`${userType}/trip/${tripID}`));
    if (err) {
      toast.error('Routes not loaded. Please try again!');
      return rejectWithValue(err);
    }
    dispatch(showRouteDetailsModal(true));
    return data?.data;
  },
);

export const fetchTripReportData = createAsyncThunk<any, { tripID: number; licenseNo: string }>(
  'routes/get-report-details',
  async ({ tripID }, { rejectWithValue, getState }) => {
    const userType = getUserType(getState() as RootState);
    if (userType === 'admin') {
      return rejectWithValue('Not available for this user type');
    }
    const [err, data] = await to(APIService.get(`${userType}/trip/${tripID}`));
    if (err) {
      toast.error('Could not fetch trip report. Please try again!');
      return rejectWithValue(err);
    }
    return data?.data;
  },
);

export const fetchRouteConfirmationDetails = createAsyncThunk<any, { tripID: number }>(
  'routes/confirmation-details',
  async ({ tripID }, { rejectWithValue, getState }) => {
    const userType = getUserType(getState() as RootState);
    if (userType === 'admin') return rejectWithValue('Not available for this user type');
    const [err, data] = await to(APIService.get(`${userType}/trip/${tripID}/confirmation-images`));
    if (err) {
      toast.error('Routes not loaded. Please try again!');
      return rejectWithValue(err);
    }
    const body = data?.data;
    return Array.isArray(body) ? body : body?.data ?? [];
  },
);

export const tripsSlice = createSlice({
  name: 'trips',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchRoutes.pending, (s) => {
      s.loading = true;
    });
    builder.addCase(fetchRoutes.rejected, (s) => {
      s.loading = false;
    });
    builder.addCase(fetchRoutes.fulfilled, (s, { payload }) => {
      s.loading = false;
      s.routes = payload;
    });

    builder.addCase(fetchRouteDetails.pending, (s) => {
      s.selectedRouteLoading = true;
      s.selectedRoute = {};
    });
    builder.addCase(fetchRouteDetails.rejected, (s) => {
      s.selectedRouteLoading = false;
    });
    builder.addCase(fetchRouteDetails.fulfilled, (s, { payload }) => {
      s.selectedRouteLoading = false;
      s.selectedRoute = payload;
    });

    builder.addCase(fetchTripReportData.pending, (s) => {
      s.selectedRouteLoading = true;
      s.selectedRoute = {};
    });
    builder.addCase(fetchTripReportData.rejected, (s) => {
      s.selectedRouteLoading = false;
    });
    builder.addCase(fetchTripReportData.fulfilled, (s, { payload }) => {
      s.selectedRouteLoading = false;
      s.selectedRoute = payload;
    });

    builder.addCase(fetchRouteConfirmationDetails.fulfilled, (s, { payload }) => {
      s.confirmationDetailsData = payload;
    });
  },
});

const tripsStore = (store: RootState): TripsState => store.trips;
export const getRoutesLoading = createSelector(tripsStore, (s) => s.loading);
export const getRoutes = createSelector(tripsStore, (s) => s.routes);
export const getSelectedRouteDetails = createSelector(tripsStore, (s) => s.selectedRoute);
export const getSelectedRouteDetailsLoading = createSelector(tripsStore, (s) => s.selectedRouteLoading);
export const getRouteConfirmationDetailsData = createSelector(tripsStore, (s) => s.confirmationDetailsData);

export default tripsSlice.reducer;
